import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, LifeBuoy, CalendarClock, Sparkles, Terminal, Inbox, X } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

const META = {
  warranty: { icon: ShieldCheck, color: "#FF3B30", label: "Warranty" },
  ticket: { icon: LifeBuoy, color: "#FF6B00", label: "Ticket" },
  booking: { icon: CalendarClock, color: "#FFB000", label: "Booking" },
  lead: { icon: Sparkles, color: "#FF8A33", label: "Lead" },
  diagnostic: { icon: Terminal, color: "#FF9E1B", label: "Diagnostic" },
};

function summary(r) {
  const d = r.data || {};
  switch (r.type) {
    case "warranty": return d.device || d.issue || "Warranty claim";
    case "ticket": return d.subject || "Support ticket";
    case "booking": return `${d.service || "Service"} · ${d.date || ""} ${d.time || ""}`;
    case "lead": return `${d.name || ""}${d.company ? " · " + d.company : ""}` || "Lead";
    case "diagnostic": return d.title || "Diagnostic playbook";
    default: return r.type;
  }
}

export function ContextRail({ records, open, onClose }) {
  const content = (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 h-16 border-b border-[var(--border)] shrink-0">
        <Inbox size={16} className="text-[var(--primary)]" />
        <span className="font-display font-semibold text-sm flex-1">Session Records</span>
        <span className="font-mono-tech text-xs px-2 py-0.5 rounded-md bg-black/30 border border-[var(--border)] text-[var(--text-secondary)]">{records.length}</span>
        <button onClick={onClose} className="lg:hidden text-[var(--text-muted)] hover:text-white" data-testid="context-rail-close"><X size={18} /></button>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-2" data-testid="context-rail">
          {records.length === 0 && (
            <div className="text-center py-16 px-4">
              <Inbox size={30} className="mx-auto text-[var(--text-muted)] mb-3" />
              <p className="text-sm text-[var(--text-muted)]">Tickets, bookings, warranty claims and leads created in this conversation will appear here.</p>
            </div>
          )}
          {records.map((r) => {
            const m = META[r.type] || META.ticket;
            return (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}
                className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-3 min-w-0 overflow-hidden"
                data-testid={`record-${r.type}`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <m.icon size={14} style={{ color: m.color }} />
                  <span className="text-[10px] font-mono-tech uppercase tracking-widest text-[var(--text-muted)]">{m.label}</span>
                  <span className="ml-auto font-mono-tech text-[11px]" style={{ color: m.color }}>{r.ref}</span>
                </div>
                <p className="text-sm text-[var(--text-primary)] truncate">{summary(r)}</p>
              </motion.div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );

  return (
    <>
      <aside className="hidden xl:flex w-80 flex-col border-l border-[var(--border)] bg-[var(--surface-2)]/60 glass shrink-0">
        {content}
      </aside>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/60 xl:hidden" onClick={onClose}
          >
            <motion.aside
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="absolute right-0 top-0 h-full w-80 max-w-[85vw] bg-[var(--surface-2)] glass border-l border-[var(--border)]"
              onClick={(e) => e.stopPropagation()}
            >
              {content}
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
